import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  idToken: string | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (u) {
        u.getIdToken().then(setIdToken).catch(() => setIdToken(null));
      } else {
        setIdToken(null);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      user.getIdToken(true).then(setIdToken).catch(() => setIdToken(null));
    }, 55 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      const u = auth.currentUser;
      if (u) setIdToken(await u.getIdToken());
    } catch (error: any) {
      console.error('Login error:', error);
    }
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    setIdToken(null);
  };

  const getToken = async (): Promise<string | null> => {
    const u = auth.currentUser;
    if (!u) return null;
    try {
      return await u.getIdToken(true);
    } catch {
      return null;
    }
  };

  const value: AuthContextValue = {
    user,
    loading,
    idToken,
    signInWithGoogle,
    signOut,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
